/**
 * AIWARI zone catalogue (R130 WotLK 3.3.5a scale).
 *
 * R223: ALL display names переименованы с WotLK 3.3.5a IP на AIWARI custom
 * lore — Greenwald Vale (запад), Sharpcrest Steppe (восток), Stormveil
 * Crossroads (центр). Internal IDs кеппуются stable для backward compat.
 *
 * World is 7000m × 3500m. West = Greenwald Vale (~3300×3500), East =
 * Sharpcrest Steppe (~3300×3500), centre = neutral Stormveil Crossroads.
 *
 * Zone IDs are kept stable for backward-compat with ZoneToast image
 * mappings and server pvp logic — only bounds were rescaled.
 *
 * `pvp`:
 *   safe       — cannot attack other players
 *   contested  — open RvR, all sides hostile to each other
 *   warzone    — like contested + active capture objective
 */

export type PvPRule = 'safe' | 'contested' | 'warzone';

/**
 * R232q22 (WorldMap): kinds of map points-of-interest rendered on the
 * "Зона" view of the WorldMap modal. Mirrors classic 3.3.5a map-pin set.
 *   questgiver — yellow `!` (offers a quest)
 *   turnin     — yellow `?` (a quest can be turned in)
 *   city       — gold star pin (capital / hub)
 *   flight     — gold flight master dot
 *   dungeon    — red gate (instance entrance)
 *   mail       — envelope glyph (mailbox)
 *   trainer    — open-book glyph (class trainer)
 */
export type ZonePoiKind =
  | 'questgiver'
  | 'turnin'
  | 'city'
  | 'flight'
  | 'dungeon'
  | 'mail'
  | 'trainer';

export interface ZonePoi {
  kind: ZonePoiKind;
  /** World-space X (matches Zone.bounds.minX..maxX). */
  x: number;
  /** World-space Z (matches Zone.bounds.minZ..maxZ). */
  z: number;
  name: string;
}

export interface Zone {
  id: string;
  name: { ru: string; en: string };
  /** AABB in world space (XZ plane). */
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  recommendedLevel: { min: number; max: number };
  pvp: PvPRule;
  /** Hex colour for minimap + signposts. */
  colour: string;
  /** Side that "owns" this zone visually. null for neutral. */
  ownedBy: 'tidal' | 'rooted' | 'anchorless' | null;
  blurb: { ru: string; en: string };
  /**
   * R232q22 (WorldMap): optional POI markers shown on the «Зона» view.
   * Empty by default — populated lazily as we ingest NPC data per zone.
   */
  pois?: ZonePoi[];
}

// Continent-scale zones (full Alliance / Horde halves).
const CONTINENTS: Zone[] = [
  {
    id: 'tidal-spawn',
    name: { ru: 'Зелёный Дол', en: 'Greenwald Vale' },
    bounds: { minX: -3500, maxX: -200, minZ: -1750, maxZ: 1750 },
    recommendedLevel: { min: 1, max: 10 },
    pvp: 'safe',
    colour: '#5a8a3a',
    ownedBy: 'tidal',
    blurb: {
      ru: 'Зелёные холмы Зелёного Дола. Дубовые рощи, ручьи, овечьи пастбища. От Восточного Аббатства дороги ведут через деревню Меднорощь к воротам Львиной Твердыни. Дом людей и дворфов Стихии Прилива, уровни 1-10.',
      en: 'Green hills of Greenwald. Oak groves, streams, sheep pastures. From Eastlight Abbey roads lead through Coppergrove village to the gates of Lionhold. Home of Tidal humans and dwarves, levels 1-10.',
    },
  },
  {
    id: 'anchorless-spawn',
    name: { ru: 'Острогривая Степь', en: 'Sharpcrest Steppe' },
    bounds: { minX: 200, maxX: 3500, minZ: -1750, maxZ: 1750 },
    recommendedLevel: { min: 1, max: 10 },
    pvp: 'safe',
    colour: '#b85b2a',
    ownedBy: 'anchorless',
    blurb: {
      ru: 'Рыжие выжженные земли Острогривой Степи. Сухой ветер, кактусы, скалы и шатры пустынных кланов. От Долины Испытаний дорога ведёт через Острый Гребень к воротам Цитадели Бранного Колеса. Дом изгнанников Стихии Одичалой, уровни 1-10.',
      en: 'Red scorched lands of Sharpcrest. Dry wind, cacti, cliffs and tribal tents. From the Trial Vale a road leads through Sharpridge to the gates of Wartus Citadel. Home of Anchorless exiles, levels 1-10.',
    },
  },
  {
    id: 'stormveil-crossroads',
    name: { ru: 'Грозовой Перекрёсток', en: 'Stormveil Crossroads' },
    bounds: { minX: -200, maxX: 200, minZ: -500, maxZ: 500 },
    recommendedLevel: { min: 5, max: 30 },
    pvp: 'contested',
    colour: '#F87171',
    ownedBy: null,
    blurb: {
      ru: 'Там, где сходятся Прилив и Одичалые, идёт вечная война. PvP открыто.',
      en: 'Where Tidal and Anchorless meet, the war is eternal. Open PvP.',
    },
  },
];

// Capital cities (sub-zones inside continents).
const CITIES: Zone[] = [
  {
    id: 'stormwind',
    name: { ru: 'Львиная Твердыня', en: 'Lionhold' },
    bounds: { minX: -3500, maxX: -2700, minZ: -500, maxZ: 500 },
    recommendedLevel: { min: 1, max: 60 },
    pvp: 'safe',
    colour: '#3B82F6',
    ownedBy: 'tidal',
    blurb: {
      ru: 'Великая столица Стихии Прилива. Каменные стены, башни с синими коническими крышами, мраморные мосты, фонтаны и Собор Сияния Прибоя.',
      en: 'Great Tidal capital. Stone walls, blue conical-roof towers, marble bridges, fountains and the Cathedral of Tideglow.',
    },
  },
  {
    id: 'orgrimmar',
    name: { ru: 'Цитадель Бранного Колеса', en: 'Wartus Citadel' },
    bounds: { minX: 2700, maxX: 3500, minZ: -500, maxZ: 500 },
    recommendedLevel: { min: 1, max: 60 },
    pvp: 'safe',
    colour: '#DC2626',
    ownedBy: 'anchorless',
    blurb: {
      ru: 'Столица Стихии Одичалой. Деревянные шипованные стены, башни с факелами, красные знамёна с чёрным шестизубым колесом.',
      en: 'Capital of the Anchorless. Wooden spiked walls, towers with torches, red banners with the black six-toothed wheel.',
    },
  },
  // R132+R223: 6 столиц + sub-capitals — нормальные русские названия, без WotLK 3.3.5a-WoW IP
  {
    id: 'ironforge',
    name: { ru: 'Железный Костёр', en: 'IronPyre' },
    bounds: { minX: -3500, maxX: -2900, minZ: -1700, maxZ: -1300 },
    recommendedLevel: { min: 1, max: 60 },
    pvp: 'safe',
    colour: '#a16207',
    ownedBy: 'tidal',
    blurb: {
      ru: 'Великая столица дворфов внутри горы Хладного Камня. Бесконечные кузницы, золотые залы и Великий Горн.',
      en: 'Great dwarven capital inside Coldstone Mountain. Endless forges, golden halls, the Great Forge.',
    },
  },
  {
    id: 'darnassus',
    name: { ru: 'Лунная Ива', en: 'Moonwillow' },
    bounds: { minX: 700, maxX: 1100, minZ: 1100, maxZ: 1500 },
    recommendedLevel: { min: 1, max: 60 },
    pvp: 'safe',
    colour: '#a78bfa',
    ownedBy: 'tidal',
    blurb: {
      ru: 'Город лесных эльфов на ветвях Великой Ивы. Мраморные дома, фиолетовый свет, лунные пруды.',
      en: 'Forest elf city on the boughs of the Great Willow. Marble homes, violet light, moonpools.',
    },
  },
  {
    id: 'undercity',
    name: { ru: 'Тёмный Склеп', en: 'Crypthold' },
    bounds: { minX: -1700, maxX: -1300, minZ: 1300, maxZ: 1700 },
    recommendedLevel: { min: 1, max: 60 },
    pvp: 'safe',
    colour: '#84CC16',
    ownedBy: 'anchorless',
    blurb: {
      ru: 'Подземный лабиринт нежити под руинами Падшего Королевства. Зеленоватая чума в каналах.',
      en: 'Undead underground maze beneath the Fallen Kingdom\'s ruins. Green plague in the canals.',
    },
  },
  {
    id: 'thunder-bluff',
    name: { ru: 'Грозовой Утёс', en: 'Stormcliff' },
    bounds: { minX: 200, maxX: 600, minZ: 700, maxZ: 1100 },
    recommendedLevel: { min: 1, max: 60 },
    pvp: 'safe',
    colour: '#92400E',
    ownedBy: 'anchorless',
    blurb: {
      ru: 'Город степных вождей на четырёх плато, соединённых верёвочными мостами. Тотемы, костры, духи предков.',
      en: 'Steppe-chieftain city on four mesas linked by rope bridges. Totems, bonfires, ancestor spirits.',
    },
  },
  {
    id: 'gnomeregan',
    name: { ru: 'Шестерённый Город', en: 'Cogwarren' },
    bounds: { minX: -3500, maxX: -3100, minZ: -1300, maxZ: -900 },
    recommendedLevel: { min: 25, max: 35 },
    pvp: 'contested',
    colour: '#F472B6',
    ownedBy: 'tidal',
    blurb: {
      ru: 'Заражённая радиацией столица механиков. Захвачена ползунами-троггами, гномы готовят отбить родину.',
      en: 'Radiation-infested machinist capital. Overrun by troggs; gnomes prepare to retake their home.',
    },
  },
  {
    id: 'sen-jin-village',
    name: { ru: 'Прибойная Деревня', en: 'Tikwave' },
    bounds: { minX: 2700, maxX: 3100, minZ: 700, maxZ: 1100 },
    recommendedLevel: { min: 1, max: 10 },
    pvp: 'safe',
    colour: '#06B6D4',
    ownedBy: 'anchorless',
    blurb: {
      ru: 'Тропическая деревня на сваях у моря. Островные племена Чёрного Копья, ритуалы прибоя.',
      en: 'Tropical seaside stilt village. Blackspear island tribes, surf rituals.',
    },
  },
];

// Sub-zones (villages + landmarks inside continents).
const SUBZONES: Zone[] = [
  {
    id: 'goldshire',
    name: { ru: 'Меднорощь', en: 'Coppergrove' },
    bounds: { minX: -1700, maxX: -1300, minZ: -200, maxZ: 200 },
    recommendedLevel: { min: 1, max: 8 },
    pvp: 'safe',
    colour: '#84CC16',
    ownedBy: 'tidal',
    blurb: {
      ru: 'Деревня в центре Зелёного Дола. Таверна "Гордая Грива", кузница, лавка алхимика. Главный квестовый хаб 6-10.',
      en: 'Village in central Greenwald Vale. Proud Mane Tavern, blacksmith, alchemist shop. Main quest hub 6-10.',
    },
  },
  {
    id: 'northshire',
    name: { ru: 'Восточное Аббатство', en: 'Eastlight Abbey' },
    bounds: { minX: -800, maxX: -400, minZ: -1100, maxZ: -700 },
    recommendedLevel: { min: 1, max: 5 },
    pvp: 'safe',
    colour: '#A3E635',
    ownedBy: 'tidal',
    blurb: {
      ru: 'Стартовая долина людей Прилива. Каменное аббатство, кладбище, шахта Эхо-Каньон. Сюда приходят новички 1-5.',
      en: 'Tidal human starter valley. Stone abbey, graveyard, Echo Canyon mine. Newbies 1-5.',
    },
  },
  {
    id: 'eastvale',
    name: { ru: 'Дубокрайская Лесопилка', en: 'Oakedge Mill' },
    bounds: { minX: -1100, maxX: -700, minZ: 700, maxZ: 1100 },
    recommendedLevel: { min: 8, max: 12 },
    pvp: 'safe',
    colour: '#65A30D',
    ownedBy: 'tidal',
    blurb: {
      ru: 'Лесопилка на восточной окраине Зелёного Дола. Дубовые брёвна, медведи, бандиты Тёмного Меча.',
      en: 'Logging camp on the eastern edge of Greenwald. Oak logs, bears, Dark Sword bandits.',
    },
  },
  {
    id: 'razor-hill',
    name: { ru: 'Острый Гребень', en: 'Sharpridge Outpost' },
    bounds: { minX: 1300, maxX: 1700, minZ: -200, maxZ: 200 },
    recommendedLevel: { min: 1, max: 8 },
    pvp: 'safe',
    colour: '#C2410C',
    ownedBy: 'anchorless',
    blurb: {
      ru: 'Палисадная крепость в центре Острогривой Степи. Таверна, кузница, главный квестовый хаб 6-10. Командует вождь Каррак Шрам-Души.',
      en: 'Palisade stronghold in central Sharpcrest Steppe. Inn, forge, main quest hub 6-10. Chief Karrak Soulscar commands.',
    },
  },
  {
    id: 'valley-of-trials',
    name: { ru: 'Долина Испытаний', en: 'Trial Vale' },
    bounds: { minX: 700, maxX: 1100, minZ: 800, maxZ: 1200 },
    recommendedLevel: { min: 1, max: 5 },
    pvp: 'safe',
    colour: '#9A3412',
    ownedBy: 'anchorless',
    blurb: {
      ru: 'Стартовая долина Одичалых. Десятник Тазрил, Горнак Кулак дают первые квесты. Быки, скорпиды.',
      en: 'Anchorless starter valley. Foreman Tazril, Gornak Fist give first quests. Bulls, scorpids.',
    },
  },
  {
    id: 'senjin-village',
    name: { ru: 'Прибой', en: 'Tikwave Surf' },
    bounds: { minX: 1300, maxX: 1700, minZ: 1300, maxZ: 1700 },
    recommendedLevel: { min: 5, max: 12 },
    pvp: 'safe',
    colour: '#06B6D4',
    ownedBy: 'anchorless',
    blurb: {
      ru: 'Береговая островная деревня. Бамбук, ритуальные маски, пирс к Эхо-Островам. Мастер Гадрак и Вождь Зикван дают квесты.',
      en: 'Coastal island village. Bamboo, ritual masks, pier to Echo Isles. Master Gadrak and Chief Zikvan give quests.',
    },
  },
  // ── R232q5k: WotLK 3.3.5a-equivalent mid/high-level zones (AIWARI custom names) ──
  {
    id: 'westmarch',
    name: { ru: 'Западный Простор', en: 'Westmarch' },
    bounds: { minX: -2200, maxX: -1700, minZ: -700, maxZ: 100 },
    recommendedLevel: { min: 10, max: 20 }, pvp: 'contested', colour: '#D97706', ownedBy: 'tidal',
    blurb: { ru: 'Заброшенные пшеничные поля и шахты Мёртвых Копей. Горнисты, гноллы, бандиты Тени.', en: 'Abandoned wheat fields and Deadmines. Bugmen, gnolls, Shadow bandits.' },
  },
  {
    id: 'redrock-hills',
    name: { ru: 'Краснокаменные Холмы', en: 'Redrock Hills' },
    bounds: { minX: -1500, maxX: -1100, minZ: -1500, maxZ: -1000 },
    recommendedLevel: { min: 15, max: 25 }, pvp: 'contested', colour: '#B91C1C', ownedBy: 'tidal',
    blurb: { ru: 'Сухие красные холмы с гоблинскими ямами и кладбищем драконьих костей.', en: 'Dry red hills with goblin pits and dragon-bone graveyard.' },
  },
  {
    id: 'duskhollow',
    name: { ru: 'Сумрачная Лощина', en: 'Duskhollow' },
    bounds: { minX: -2400, maxX: -1900, minZ: -1400, maxZ: -800 },
    recommendedLevel: { min: 18, max: 30 }, pvp: 'contested', colour: '#5B21B6', ownedBy: 'tidal',
    blurb: { ru: 'Болота вечной ночи. Воргены, нежить, призрак Эль-Аэдар. Сюда не ходят без отряда.', en: 'Eternal-night marsh. Worgen, undead, the ghost of El-Aedar. Don\'t go alone.' },
  },
  {
    id: 'thorndeep',
    name: { ru: 'Терновая Низина', en: 'Thorndeep' },
    bounds: { minX: -2700, maxX: -2200, minZ: 700, maxZ: 1500 },
    recommendedLevel: { min: 30, max: 45 }, pvp: 'contested', colour: '#15803D', ownedBy: null,
    blurb: { ru: 'Пиратские джунгли. Тролли Гурубаши, охотники за головами, древние ацтекские руины.', en: 'Pirate jungle. Gurubashi trolls, headhunters, ancient temple ruins.' },
  },
  {
    id: 'lochmod',
    name: { ru: 'Лохмодское Озеро', en: 'Lochmod Lake' },
    bounds: { minX: -2700, maxX: -2200, minZ: -700, maxZ: -100 },
    recommendedLevel: { min: 10, max: 20 }, pvp: 'safe', colour: '#0EA5E9', ownedBy: 'tidal',
    blurb: { ru: 'Большое озеро в дворфийских горах. Старая плотина, кобольды, исследователи Лиги.', en: 'Great lake in dwarven hills. Old dam, kobolds, Explorers\' League.' },
  },
  {
    id: 'mireland',
    name: { ru: 'Болотина', en: 'Mireland' },
    bounds: { minX: -2700, maxX: -2200, minZ: 100, maxZ: 700 },
    recommendedLevel: { min: 20, max: 30 }, pvp: 'contested', colour: '#365314', ownedBy: 'tidal',
    blurb: { ru: 'Туманная топь. Драконыши Тёмного Железа, орки Сумеречного Молота, разбитый караван.', en: 'Foggy marsh. Dark Iron whelps, Twilight Hammer orcs, broken caravan.' },
  },
  {
    id: 'searing-gorge',
    name: { ru: 'Тлеющее Ущелье', en: 'Searing Gorge' },
    bounds: { minX: -1700, maxX: -1100, minZ: 1100, maxZ: 1700 },
    recommendedLevel: { min: 45, max: 50 }, pvp: 'contested', colour: '#DC2626', ownedBy: null,
    blurb: { ru: 'Лавовое ущелье. Гоблинская станция Кальм-1, элементали огня, гора Чёрной Скалы видна.', en: 'Lava gorge. Goblin Thorium Brotherhood camp, fire elementals, Blackrock Mountain looms.' },
  },
  {
    id: 'burnsteppe',
    name: { ru: 'Пылающие Степи', en: 'Burnsteppe' },
    bounds: { minX: -1100, maxX: -500, minZ: 1100, maxZ: 1700 },
    recommendedLevel: { min: 50, max: 55 }, pvp: 'warzone', colour: '#991B1B', ownedBy: null,
    blurb: { ru: 'Выжженные пустоши у Чёрной Скалы. Огненные псы, чёрные драконыши, лагерь Огнедара.', en: 'Scorched wasteland near Blackrock. Fire hounds, black whelps, Flamegard camp.' },
  },
  {
    id: 'cinderlands',
    name: { ru: 'Выжженные Земли', en: 'Cinderlands' },
    bounds: { minX: -500, maxX: 100, minZ: 1100, maxZ: 1700 },
    recommendedLevel: { min: 45, max: 55 }, pvp: 'contested', colour: '#7C2D12', ownedBy: null,
    blurb: { ru: 'Пограничье с Тёмным порталом. Демоны, изгнанники, Костяные пустоши.', en: 'Borderland with the Dark Portal. Demons, exiles, Bonewastes.' },
  },
  {
    id: 'sorrow-marsh',
    name: { ru: 'Болото Скорби', en: 'Sorrow Marsh' },
    bounds: { minX: -1700, maxX: -1100, minZ: 700, maxZ: 1100 },
    recommendedLevel: { min: 35, max: 45 }, pvp: 'contested', colour: '#1E3A8A', ownedBy: null,
    blurb: { ru: 'Зыбкая трясина. Останки Каражана видны на горизонте. Атал\'ари тролли, мерлоки, древние ритуалы.', en: 'Restless bog. Karazhan ruins on the horizon. Atal\'ari trolls, merlocs, ancient rites.' },
  },
  {
    id: 'hilsbrook',
    name: { ru: 'Хилсбрукские Предгорья', en: 'Hilsbrook Foothills' },
    bounds: { minX: -2200, maxX: -1700, minZ: 700, maxZ: 1300 },
    recommendedLevel: { min: 20, max: 30 }, pvp: 'warzone', colour: '#B45309', ownedBy: null,
    blurb: { ru: 'Контрольная зона PvP. Тарренова Мельница против Южнобережья. Постоянные стычки разведчиков.', en: 'Open PvP zone. Tarren Mill vs Southshore. Constant scout skirmishes.' },
  },
  {
    id: 'arathih',
    name: { ru: 'Аратийское Нагорье', en: 'Arathih Highlands' },
    bounds: { minX: -2700, maxX: -2200, minZ: 1100, maxZ: 1700 },
    recommendedLevel: { min: 30, max: 40 }, pvp: 'warzone', colour: '#92400E', ownedBy: null,
    blurb: { ru: 'Древние людские руины. Поле боя «Аратийская Котловина» — захват ресурсов.', en: 'Ancient human ruins. Arathi Basin BG — resource capture warfare.' },
  },
  {
    id: 'tirisfal-glade',
    name: { ru: 'Тирисфальские Леса', en: 'Tirisfal Glades' },
    bounds: { minX: -1500, maxX: -1100, minZ: 1100, maxZ: 1500 },
    recommendedLevel: { min: 1, max: 10 }, pvp: 'safe', colour: '#84CC16', ownedBy: 'anchorless',
    blurb: { ru: 'Стартовая зона нежити. Туманные леса, разрушенная деревня Брилль, монастырь Серебряной Длани.', en: 'Undead starter zone. Misty forest, ruined Brill village, Scarlet Monastery.' },
  },
  {
    id: 'silverpine',
    name: { ru: 'Серебряный Бор', en: 'Silverpine' },
    bounds: { minX: -1900, maxX: -1500, minZ: 1100, maxZ: 1500 },
    recommendedLevel: { min: 10, max: 20 }, pvp: 'contested', colour: '#475569', ownedBy: 'anchorless',
    blurb: { ru: 'Сосновый бор у Чумных Земель. Воргены, нежить, замок Шадоуфэнг.', en: 'Pine forest by Plaguelands. Worgen, undead, Shadowfang Keep.' },
  },
  {
    id: 'west-plagueland',
    name: { ru: 'Западные Чумные Земли', en: 'West Plagued' },
    bounds: { minX: -2400, maxX: -1900, minZ: 1500, maxZ: 1700 },
    recommendedLevel: { min: 51, max: 58 }, pvp: 'contested', colour: '#84CC16', ownedBy: null,
    blurb: { ru: 'Зачумлённые поля Андорхала. Скоулмансский монастырь, Плачущий храм, чумная грязь.', en: 'Plagued fields of Andorhal. Scholomance monastery, Weeping Cave, plague mire.' },
  },
  {
    id: 'east-plagueland',
    name: { ru: 'Восточные Чумные Земли', en: 'East Plagued' },
    bounds: { minX: -1900, maxX: -1500, minZ: 1500, maxZ: 1700 },
    recommendedLevel: { min: 53, max: 60 }, pvp: 'warzone', colour: '#16A34A', ownedBy: null,
    blurb: { ru: 'Стратхольм пал. Плачущая роща, Тиррисглейдский лес, Светлая Надежда. Полчища нежити Артаса.', en: 'Stratholme has fallen. Weeping Grove, Tyrrisglade Forest, Light\'s Hope. Arthas\' undead horde.' },
  },
  {
    id: 'wilds',
    name: { ru: 'Дикие Степи', en: 'The Wilds' },
    bounds: { minX: 700, maxX: 1300, minZ: -700, maxZ: 100 },
    recommendedLevel: { min: 10, max: 25 }, pvp: 'contested', colour: '#CA8A04', ownedBy: 'anchorless',
    blurb: { ru: 'Огромная саванна. Кентавры, разбойники-флибустьеры, шахта Багровых, центр заданий Племени 10-25.', en: 'Vast savanna. Centaurs, freebooter bandits, Crossroads, Storm Tribe quest hub 10-25.' },
  },
  {
    id: 'mulhorn',
    name: { ru: 'Мульхорнские Луга', en: 'Mulhorn Plains' },
    bounds: { minX: 700, maxX: 1100, minZ: 100, maxZ: 700 },
    recommendedLevel: { min: 1, max: 10 }, pvp: 'safe', colour: '#65A30D', ownedBy: 'anchorless',
    blurb: { ru: 'Стартовая зона тауренов. Священный Кодо, фермы Бладхуф, Камень Тысячи Тотемов.', en: 'Tauren starter zone. Sacred Kodo, Bloodhoof farms, Thousand Totems Stone.' },
  },
  {
    id: 'stoneclaw',
    name: { ru: 'Когтистые Горы', en: 'Stoneclaw Mountains' },
    bounds: { minX: 700, maxX: 1300, minZ: 100, maxZ: 700 },
    recommendedLevel: { min: 15, max: 30 }, pvp: 'contested', colour: '#737373', ownedBy: null,
    blurb: { ru: 'Каменистый хребет. Граница между Степями и Ясеневым Лесом. Кентавры, харпии, лесопилки.', en: 'Rocky ridge. Border between Wilds and Ashpine. Centaurs, harpies, lumber mills.' },
  },
  {
    id: 'ashpine',
    name: { ru: 'Ясеневый Лес', en: 'Ashpine Forest' },
    bounds: { minX: 700, maxX: 1100, minZ: 800, maxZ: 1100 },
    recommendedLevel: { min: 18, max: 30 }, pvp: 'warzone', colour: '#16A34A', ownedBy: 'tidal',
    blurb: { ru: 'Древний эльфийский лес. Святилище Луны, Лесники Серебряной Стрелы, война с лесопилками Племени.', en: 'Ancient elf forest. Moon Sanctuary, Silver Arrow rangers, war over Storm Tribe mills.' },
  },
  {
    id: 'needlespires',
    name: { ru: 'Тысяча Игл', en: 'Needlespires' },
    bounds: { minX: 1300, maxX: 1700, minZ: 700, maxZ: 1100 },
    recommendedLevel: { min: 25, max: 35 }, pvp: 'contested', colour: '#A16207', ownedBy: null,
    blurb: { ru: 'Лабиринт каменных столбов и каньонов. Гонки на ракетах, шахта Освобождения, бои с Зенлектами.', en: 'Maze of stone columns and canyons. Rocket races, Liberation Mine, fights with Twilights.' },
  },
  {
    id: 'dustmire',
    name: { ru: 'Пыльная Топь', en: 'Dustmire' },
    bounds: { minX: 700, maxX: 1300, minZ: 1300, maxZ: 1700 },
    recommendedLevel: { min: 35, max: 45 }, pvp: 'contested', colour: '#0F766E', ownedBy: null,
    blurb: { ru: 'Тропические болота с драконами. Тёмная Чешуя, Шепчущий Камыш, Брекенвейл.', en: 'Tropical bogs with dragons. Dark Scale, Whispering Reed, Brackenvale.' },
  },
  {
    id: 'wildshore',
    name: { ru: 'Дикий Берег', en: 'Wildshore' },
    bounds: { minX: 1300, maxX: 1700, minZ: -1500, maxZ: -1100 },
    recommendedLevel: { min: 40, max: 50 }, pvp: 'contested', colour: '#15803D', ownedBy: null,
    blurb: { ru: 'Древний эльфийский лес у моря. Огры, Дикорай, Хищные Лозы. Гордунни клан огров.', en: 'Ancient seaside elf forest. Ogres, Wildgrove, Predator Vines. Gordunni ogre clan.' },
  },
  {
    id: 'sunsands',
    name: { ru: 'Солнечные Пески', en: 'Sunsands' },
    bounds: { minX: 1300, maxX: 1700, minZ: 1100, maxZ: 1700 },
    recommendedLevel: { min: 40, max: 50 }, pvp: 'contested', colour: '#FBBF24', ownedBy: null,
    blurb: { ru: 'Великая пустыня. Гадоксы, гномьи руины, Зул\'Каррак, Танарисский Караван.', en: 'Great desert. Gadox lizards, gnomish ruins, Zul\'Farrak, Tanaris Caravan.' },
  },
  {
    id: 'crater-of-wonder',
    name: { ru: 'Кратер Чудес', en: 'Crater of Wonder' },
    bounds: { minX: -100, maxX: 500, minZ: 1100, maxZ: 1700 },
    recommendedLevel: { min: 48, max: 55 }, pvp: 'contested', colour: '#22C55E', ownedBy: null,
    blurb: { ru: 'Затерянный кратер с динозаврами и древней магией. Сланцелов, Малыш Эль, Вулкан Огня.', en: 'Lost crater with dinosaurs and ancient magic. Slatekiln, Little El, Fire Volcano.' },
  },
  {
    id: 'crystalwastes',
    name: { ru: 'Кристальные Пустоши', en: 'Crystalwastes' },
    bounds: { minX: 500, maxX: 1100, minZ: 1500, maxZ: 1700 },
    recommendedLevel: { min: 55, max: 60 }, pvp: 'warzone', colour: '#A855F7', ownedBy: null,
    blurb: { ru: 'Зимние ключи Силитуса. Анубисат, Кристальный пик, ключи к раидсу С\'Кваль.', en: 'Silithus crystal wastes. Anubisath, Crystal Peak, keys to Ahn\'Qiraj raid.' },
  },
  {
    id: 'moonglade',
    name: { ru: 'Лунная Поляна', en: 'Moonglade' },
    bounds: { minX: 200, maxX: 600, minZ: 1500, maxZ: 1700 },
    recommendedLevel: { min: 55, max: 60 }, pvp: 'safe', colour: '#A78BFA', ownedBy: null,
    blurb: { ru: 'Священная роща друидов. Седрик-Луна, Малы\'таф Огонь Кенария, Озеро Сланцев. Только мирный созерцать.', en: 'Sacred druid grove. Cedric-Moon, Maly\'taf Cenarius Flame, Slate Lake. Strictly peaceful.' },
  },
];

// Order matters in zoneAt() — sub-zones must shadow continents, so list
// the smallest first in the iteration order.
export const ZONES: Zone[] = [...SUBZONES, ...CITIES, ...CONTINENTS];

export function zoneAt(x: number, z: number): Zone | null {
  for (const z2 of ZONES) {
    if (x >= z2.bounds.minX && x <= z2.bounds.maxX && z >= z2.bounds.minZ && z <= z2.bounds.maxZ) {
      return z2;
    }
  }
  return null;
}

export function zoneById(id: string): Zone | undefined {
  return ZONES.find((z) => z.id === id);
}
